import { Alert, Button } from "flowbite-react";
import axiosInstance from "../config/axiosInstance";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApplication } from "../contexts/ApplicationContext";

const CURRENCY_FORMATTER = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const safeDisplayValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return value;
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return CURRENCY_FORMATTER.format(parsed);
};

const billingTypeLabel = (type) => {
  if (!type) return "-";
  const normalized = type.toLowerCase();
  if (normalized.includes("credit")) return "Cartão de crédito";
  if (normalized.includes("pix")) return "PIX / QR Code";
  if (normalized.includes("boleto")) return "Boleto";
  return type;
};

const intervalLabel = (months) => {
  if (!months || months <= 0) return "-";
  if (months === 1) return "Mensal";
  if (months === 3) return "Trimestral";
  if (months === 12) return "Anual";
  return `${months} meses`;
};

const intervalDescriptor = (months) => {
  if (months === 1) return "mensal";
  if (months === 3) return "trimestral";
  if (months === 12) return "anual";
  if (months > 1) return `${months} meses`;
  return "-";
};

const FEATURE_BADGE_LIMIT = 5;
const MAX_FEATURES_PREVIEW = 6;

const ManageSubscriptionPage = () => {
  const { user } = useApplication();

  const navigate = useNavigate();

  const company = user?.Company || {};
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [isPlanModalOpen, setPlanModalOpen] = useState(false);
  const [planModalSelection, setPlanModalSelection] = useState({
    planId: null,
    cycleId: null,
  });
  const [selectedInterval, setSelectedInterval] = useState(12);
  const [hasAutoOpenedModal, setHasAutoOpenedModal] = useState(false);
  const [expandedPlanId, setExpandedPlanId] = useState(null);

  useEffect(() => {
    let active = true;

    const fetchSubscription = async () => {
      try {
        const { data } = await axiosInstance.get("/assinatura");
        if (!active) return;

        setSubscriptionData(data?.data ?? null);
        setSubscriptionError(null);
      } catch (error) {
        if (!active) return;
        console.error("Erro ao carregar dados de assinatura", error);
        setSubscriptionError(error);
      } finally {
        if (!active) return;
        setLoadingSubscription(false);
      }
    };

    fetchSubscription();

    return () => {
      active = false;
    };
  }, []);

  const subscriptionPayload = subscriptionData || {};
  const chargeInfo =
    subscriptionPayload.charge || subscriptionPayload.subscription;

  const selectedPlan = subscriptionPayload.selectedPlan || null;
  const selectedPlanCycle = subscriptionPayload.selectedPlanCycle || null;
  const availablePlans = subscriptionPayload.availablePlans || [];
  const selectedPlanId =
    subscriptionPayload.planId || selectedPlan?.id || company?.planId || null;
  const selectedPlanCycleId =
    subscriptionPayload.planCycleId || selectedPlanCycle?.id || null;
  const planName =
    subscriptionPayload.planName ||
    selectedPlan?.name ||
    company?.planName ||
    company?.plan?.name ||
    company?.planType ||
    company?.subscriptionPlan ||
    "Plano não informado";
  const planDescription =
    selectedPlan?.description || company?.planDescription || "";

  const planModalCycle = useMemo(() => {
    const plan = availablePlans.find((p) => p.id === planModalSelection.planId);
    if (!plan) return null;
    const cycle = (plan.cycles || []).find(
      (c) => c.id === planModalSelection.cycleId
    );
    return cycle ? { plan, cycle } : null;
  }, [availablePlans, planModalSelection]);

  const goToPaymentPage = (plan, cycle) => {
    if (!plan || !cycle) return;
    navigate(`/assinatura/pagamento?planId=${plan.id}&cycleId=${cycle.id}`, {
      state: {
        planName: plan.name,
        planDescription: plan.description,
        cycleIntervalMonths: cycle.intervalMonths,
      },
    });
  };

  useEffect(() => {
    setPlanModalSelection({
      planId: selectedPlanId,
      cycleId: selectedPlanCycleId,
    });
  }, [selectedPlanId, selectedPlanCycleId]);

  const findCycleForPlan = (plan, interval) =>
    (plan?.cycles || []).find((cycle) => cycle.intervalMonths === interval);

  const cycleOptions = useMemo(() => {
    const monthsSet = new Set();
    availablePlans.forEach((plan) => {
      (plan.cycles || []).forEach((cycle) => {
        const monthValue = Number(cycle.intervalMonths);
        if (Number.isFinite(monthValue)) {
          monthsSet.add(monthValue);
        }
      });
    });
    return Array.from(monthsSet).sort((a, b) => a - b);
  }, [availablePlans]);

  const displayedIntervals =
    cycleOptions.length > 0 ? cycleOptions : [12, 3, 1];

  useEffect(() => {
    if (displayedIntervals.length === 0) return;
    if (displayedIntervals.includes(selectedInterval)) return;
    const preferred = displayedIntervals.includes(12)
      ? 12
      : displayedIntervals[displayedIntervals.length - 1];
    setSelectedInterval(preferred);
  }, [displayedIntervals, selectedInterval]);

  const installmentTextForCycle = (cycle) => {
    if (!cycle) return "";
    const installments = cycle.intervalMonths;
    const base =
      cycle.billingType && cycle.billingType.toLowerCase().includes("credit")
        ? "Cartão de crédito"
        : cycle.billingType || "Cartão de crédito";
    if (installments > 1) {
      const installmentValue = cycle.price / installments;
      return `${installments}x de ${formatCurrency(installmentValue)}`;
    }
    return formatCurrency(cycle.price);
  };

  useEffect(() => {
    const plan = availablePlans.find((p) => p.id === planModalSelection.planId);
    if (!plan) return;
    const cycle = findCycleForPlan(plan, selectedInterval);
    if (!cycle) return;
    setPlanModalSelection((prev) => ({
      ...prev,
      cycleId: cycle.id,
    }));
  }, [selectedInterval, availablePlans, planModalSelection.planId]);

  const hasLinkedPlan = Boolean(company?.planId);

  useEffect(() => {
    if (!hasLinkedPlan && availablePlans.length > 0 && !hasAutoOpenedModal) {
      setPlanModalOpen(true);
      setHasAutoOpenedModal(true);
    }
  }, [availablePlans.length, hasAutoOpenedModal, hasLinkedPlan]);

  const expirationValue =
    subscriptionPayload.planExpirationDate || company?.planExpirationDate;
  const expirationDate = expirationValue ? moment(expirationValue) : null;
  const expirationLabel = expirationDate
    ? expirationDate.format("DD/MM/YYYY")
    : "Não informado";
  const daysRemaining =
    expirationDate !== null
      ? Math.max(expirationDate.diff(moment(), "days") + 1, 0)
      : null;
  const isTrialExpired =
    expirationDate !== null && moment().isAfter(expirationDate);

  const activeLicenses =
    subscriptionPayload.activeLicenses ??
    company?.activeLicenses ??
    company?.licensesInUse ??
    company?.currentLicenses ??
    company?.licenseCount ??
    company?.activeSeats ??
    company?.usersCount;

  const totalLicenses =
    subscriptionPayload.licenseLimit ??
    company?.maxLicenses ??
    company?.licenseLimit ??
    company?.seats ??
    company?.totalLicenses ??
    company?.licensePackage ??
    company?.companyLicenses;

  const billingPortalUrl =
    company?.billingPortalUrl || "https://app.relinqsales.com.br/assinatura";
  const fallbackContactUrl = "https://wa.me/+5515996730404";
  const actionUrls = subscriptionPayload.actionUrls || {};
  const manageUrl = actionUrls.manage || billingPortalUrl;
  const cancelUrl = actionUrls.cancel || manageUrl;
  const upgradeUrl = actionUrls.upgrade || manageUrl;
  const contactUrl = actionUrls.contact || fallbackContactUrl;

  const openLink = (url) => () => {
    if (!url || typeof window === "undefined") return;
    window.open(url, "_blank", "noopener");
  };

  const appendParamsToUrl = (url, params = {}) => {
    if (!url || !params) return url;
    const search = new URLSearchParams(params).toString();
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${search}`;
  };

  const handleChoosePlan = () => {
    if (!planModalSelection.planId || !planModalSelection.cycleId) return;
    const checkoutUrl = appendParamsToUrl(manageUrl, {
      planId: planModalSelection.planId,
      cycleId: planModalSelection.cycleId,
    });
    openLink(checkoutUrl)();
    setPlanModalOpen(false);
  };

  const subscriptionStatus =
    subscriptionPayload.planStatus ||
    company?.subscriptionStatus ||
    company?.planStatus ||
    "Ativo";

  const nextBillingValue =
    subscriptionPayload.nextBillingDate ||
    company?.nextBillingDate ||
    expirationValue;
  const nextBillingMoment = nextBillingValue
    ? moment(nextBillingValue)
    : expirationDate;
  const nextBillingLabel = nextBillingMoment
    ? nextBillingMoment.format("DD/MM/YYYY")
    : expirationLabel;

  const chargeDueMoment = chargeInfo?.dueDate
    ? moment(chargeInfo.dueDate)
    : nextBillingMoment;
  const chargeDueLabel = chargeDueMoment
    ? chargeDueMoment.format("DD/MM/YYYY")
    : nextBillingLabel;
  const installmentsLabel =
    chargeInfo?.installmentCount && Number(chargeInfo.installmentCount) > 1
      ? `${chargeInfo.installmentCount}x`
      : "À vista";
  const paymentMethod = billingTypeLabel(chargeInfo?.billingType);
  const chargeAmountLabel = formatCurrency(
    chargeInfo?.value ?? subscriptionPayload.planValue
  );
  const chargeStatusLabel =
    (chargeInfo?.status && chargeInfo.status.toString().toUpperCase()) || "-";
  const selectedCycleLabel = selectedPlanCycle
    ? `${intervalLabel(selectedPlanCycle.intervalMonths)} • ${formatCurrency(
        selectedPlanCycle.price
      )}`
    : null;

  return (
    <div className="space-y-6">
      {isTrialExpired && (
        <Alert color="warning" className="rounded-3xl">
          Os 7 dias de teste encerraram. Escolha um plano e conclua o pagamento
          para continuar usando a plataforma.
        </Alert>
      )}
      <header className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-400">
          Plano ativo
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            Gerenciar assinatura
          </h1>
          <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase text-emerald-600">
            {subscriptionStatus}
          </span>
        </div>
        <p className="max-w-3xl text-sm text-slate-500">
          Veja informações sobre o plano atual, licenças em uso e ações rápidas
          para ajustar ou cancelar a assinatura.
        </p>
        {loadingSubscription && (
          <p className="text-xs text-slate-400">
            Carregando informações de cobrança...
          </p>
        )}
      </header>

      {subscriptionError && (
        <Alert color="failure" className="rounded-3xl">
          Não foi possível carregar os dados da assinatura. Tente novamente mais
          tarde.
        </Alert>
      )}

      <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Plano contratado
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                {planName}
              </h2>
              {planDescription ? (
                <p className="text-sm text-slate-500">{planDescription}</p>
              ) : null}
              {selectedCycleLabel ? (
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  {selectedCycleLabel}
                </p>
              ) : null}
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Válido até
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {expirationLabel}
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Dias restantes
              </p>
              <p className="text-2xl font-semibold text-slate-900">
                {daysRemaining !== null ? daysRemaining : "-"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Licenças ativas
              </p>
              <p className="text-2xl font-semibold text-slate-900">
                {safeDisplayValue(activeLicenses)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Limite de licenças
              </p>
              <p className="text-2xl font-semibold text-slate-900">
                {safeDisplayValue(totalLicenses)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Responsável
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {safeDisplayValue(company?.responsibleName || user?.name)}
              </p>
            </div>
          </div>

          {chargeInfo && (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Cobrança atual
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    Vencimento
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {chargeDueLabel}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    Valor total
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {chargeAmountLabel}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    Forma de pagamento
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {paymentMethod}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    Parcelas
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {installmentsLabel}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    Status
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {chargeStatusLabel}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Button className="mt-4" onClick={openLink(contactUrl)}>
            Entrar em contato
          </Button>
        </section>

        {/* <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
              Ações rápidas
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Escolha uma opção para ajustar ou conversar com nossa equipe.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Button color="light" onClick={openLink(manageUrl)}>
              Gerenciar plano
            </Button>
            <Button color="info" onClick={openLink(upgradeUrl)}>
              Fazer upgrade no plano
            </Button>

          </div>
        </section> */}
      </div>

      {isPlanModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center overflow-y-auto px-4 py-8"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.3))",
            zIndex: "500",
            marginTop: 0,
          }}
        >
          <div
            className="absolute inset-0"
            onClick={() => setPlanModalOpen(false)}
          />
          <div className="relative z-10 w-full max-w-6xl rounded-xl border border-slate-200 bg-white p-6 shadow-2xl bg-white">
            <div className="text-center">
              <h3 className="text-2xl font-semibold text-slate-900">
                Escolha o seu plano ideal
              </h3>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-1 md:gap-3 rounded-full bg-gradient-to-r from-white/90 to-orange-50 px-4 py-3 shadow-inner shadow-orange-200/70">
              {displayedIntervals.map((months) => (
                <button
                  key={months}
                  type="button"
                  onClick={() => setSelectedInterval(months)}
                  className={`flex min-w-[110px] transform flex-col items-center justify-center rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.35em] transition duration-300 focus-visible:outline-none ${
                    selectedInterval === months
                      ? "border-[#f9b388] bg-[#e45412] text-white shadow-[0_20px_30px_rgba(228,84,18,0.35)]"
                      : "border border-white/70 bg-white/90 text-slate-600 text-[10px] hover:border-[#e45412] hover:text-[#e45412]"
                  }`}
                >
                  <span
                    className={`text-xs md:text-sm font-semibold tracking-[0.2em] ${
                      selectedInterval === months
                        ? "text-white"
                        : "text-slate-800"
                    }`}
                  >
                    {intervalLabel(months)}
                  </span>
                </button>
              ))}
            </div>
            <div
              className="mt-8 grid gap-6 lg:grid-cols-3  overflow-y-scroll p-3"
              style={{ maxHeight: "600px" }}
            >
              {availablePlans.map((plan) => {
                const highlighted =
                  planModalSelection.planId === plan.id &&
                  planModalSelection.cycleId ===
                    findCycleForPlan(plan, selectedInterval)?.id;
                const cycle = findCycleForPlan(plan, selectedInterval);
                return (
                  <div
                    key={plan.id}
                    className={`flex h-full flex-col rounded-xl border border-transparent bg-gradient-to-b from-white/80 to-orange-50 p-4 shadow-md transition-all ${
                      highlighted
                        ? "border-[#e45412] bg-gradient-to-b from-[#fff7f0] to-[#ffe2ca] shadow-[0_30px_60px_rgba(228,84,18,0.25)]"
                        : "border-transparent"
                    } ${cycle ? "cursor-pointer" : "opacity-70"}`}
                    onClick={() =>
                      cycle &&
                      setPlanModalSelection({
                        planId: plan.id,
                        cycleId: cycle.id,
                      })
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-xl font-semibold text-slate-900">
                          {plan.name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {plan.description}
                        </p>
                      </div>
                    </div>
                    <div className="mt-5 text-sm text-slate-600">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.4em] text-slate-500">
                        Funcionalidades
                      </p>
                      <div className="mt-2 space-y-2">
                        {((plan.features || []).length === 0
                          ? []
                          : plan.features
                        )
                          .slice(
                            0,
                            expandedPlanId === plan.id
                              ? plan.features.length
                              : MAX_FEATURES_PREVIEW
                          )
                          .map((feature) => (
                            <div
                              key={`${plan.id}-${feature}`}
                              className="flex items-start gap-2"
                            >
                              <span className="mt-[2px] h-2 w-2 rounded-full bg-[#e45412]" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        {plan.features?.length > MAX_FEATURES_PREVIEW && (
                          <button
                            type="button"
                            className="text-xs font-semibold uppercase tracking-[0.4em] text-[#e45412]"
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedPlanId((current) =>
                                current === plan.id ? null : plan.id
                              );
                            }}
                          >
                            {expandedPlanId === plan.id
                              ? "Mostrar menos"
                              : `+${
                                  plan.features.length - MAX_FEATURES_PREVIEW
                                } funcionalidades`}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-6 flex flex-col gap-1 mb-4">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                        Investimento
                      </p>
                      <p className="text-2xl font-semibold text-slate-900">
                        {cycle ? installmentTextForCycle(cycle) : "—"}
                      </p>
                      {cycle?.intervalMonths === 12 && (
                        <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-emerald-600">
                          Plano anual com mais desconto
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      className="mt-4 self-stretch"
                      style={{ marginTop: "auto" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        goToPaymentPage(plan, cycle);
                      }}
                      disabled={!cycle}
                    >
                      Selecionar plano
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSubscriptionPage;
